// libraries
import React, { useState, useEffect, useRef } from 'react'
import clsx from 'clsx'
import dayjs from 'dayjs';

// components
import Section from '/react4xp/components/Section'
import ProductCard from '/react4xp/components/ProductCard'
import DocumentationLine from '/react4xp/components/DocumentationLine'
import ArticleCardNew from '/react4xp/components/ArticleCardNew'
import SuccessStoriesCard from '/react4xp/components/SuccessStoriesCard'
import OtherLine from '/react4xp/components/OtherLine'
import Announcer, { useAnnouncer, fillPlaceholders } from '/react4xp/components/Announcer'
// utils
import { useSmoothScroll } from '/lib/reactutils/utils'

export interface SearchResultsProps {
    title: string
    endpoint: string
    solutions: { value: string, label: string }[]
    documentationTypes: string[],
    documentationHelperText: string
    articlesTypes: { value: string, label: string }[]
    strings: {
        loadMore: string,
        placeholder: string,
        resultsForCount: string,
        filterBySolution: string,
        filterByType: string,
        loading: string, // Loading...
        resultsStatus: string, // Results: showing <<shown>> of <<total>>
        noResults: string // No results found
    }
}

type TypeOfResultValues = 'articles' | 'documentation' | 'products' | 'other'

export default function SearchResultsPage({
    title,
    endpoint,
    solutions,
    documentationTypes,
    articlesTypes,
    documentationHelperText,
    strings
}: SearchResultsProps) {
    const [hasMounted, setHasMounted] = useState(false)

    const [searchTextInput, setSearchTextInput] = useState('')
    const [searchParam, setSearchParam] = useState('')
    // per section: a shared flag made all four groups look busy at once
    const [loadingSection, setLoadingSection] = useState<TypeOfResultValues | null>(null)
    
    const [selectedSolutions, setSelectedSolutions] = useState([])
    const [selectedDocumentationTypes, setSelectedDocumentationTypes] = useState([])
    const [selectedArticleTypes, setSelectedArticleTypes] = useState([])
    const [selectedOtherTypes, setSelectedOtherTypes] = useState([])

    const [productsResults, setProductsResults] = useState([])
    const [documentationResults, setDocumentationResults] = useState([])
    const [articlesResults, setArticlesResults] = useState([])
    const [otherResults, setOtherResults] = useState([])

    // track if documentation section was previously visible
    const [wasDocumentationVisible, setWasDocumentationVisible] = useState(false)
    
    const [hasProductsNextPage, setHasProductsNextPage] = useState(true)
    const [hasDocumentationNextPage, setHasDocumentationNextPage] = useState(true)
    const [hasArticlesNextPage, setHasArticlesNextPage] = useState(true)
    const [hasOtherNextPage, setHasOtherNextPage] = useState(true)
    
    const [productsCurrentPage, setProductsCurrentPage] = useState(1)
    const [documentationCurrentPage, setDocumentationCurrentPage] = useState(1)
    const [articlesCurrentPage, setArticlesCurrentPage] = useState(1)
    const [otherCurrentPage, setOtherCurrentPage] = useState(1)

    const [productsTotal, setProductsTotal] = useState(0)
    const [documentationTotal, setDocumentationTotal] = useState(0)
    const [articlesTotal, setArticlesTotal] = useState(0)
    const [otherTotal, setOtherTotal] = useState(0)

    const [announcement, announce] = useAnnouncer()

    const gridRefs = {
        products: useRef<HTMLDivElement>(null),
        documentation: useRef<HTMLDivElement>(null),
        articles: useRef<HTMLDivElement>(null),
        other: useRef<HTMLDivElement>(null)
    }

    const pendingFocusRef = useRef<{ section: TypeOfResultValues, index: number } | null>(null)
    // last announced selection per section: the first result is not a change,
    // and the documentation effect also re-runs on its own total
    const announcedFiltersRef = useRef<{ [key: string]: string }>({})

    function announceResults(shown: number, total: number) {
        if (!shown) return announce(strings.noResults)
        announce(fillPlaceholders(strings.resultsStatus, { shown, total: total || shown }))
    }

    function announceFilter(section: TypeOfResultValues, selection: string, res) {
        const isFirstRun = !(section in announcedFiltersRef.current)
        if (announcedFiltersRef.current[section] === selection) return

        announcedFiltersRef.current[section] = selection
        if (isFirstRun) return

        announceResults((res && res.hits && res.hits.length) || 0, res && res.total)
    }

    // accordion (only appears on mobile)
    const [isSidebarProductsOpen, setIsSidebarProductsOpen] = useState<boolean>(false)
    const [isSidebarDocumentationOpen, setIsSidebarDocumentationOpen] = useState<boolean>(false)
    const [isSidebarArticlesOpen, setIsSidebarArticlesOpen] = useState<boolean>(false)
    const [isSidebarOtherOpen, setIsSidebarOtherOpen] = useState<boolean>(false)
    
    const toggleSidebarProducts = () => { setIsSidebarProductsOpen((prev) => !prev) }
    const toggleSidebarDocumentation = () => { setIsSidebarDocumentationOpen((prev) => !prev) }
    const toggleSidebarArticles = () => { setIsSidebarArticlesOpen((prev) => !prev) }
    const toggleSidebarOther = () => { setIsSidebarOtherOpen((prev) => !prev) }
    
    // smooth scroll (when clicking the anchor links)
    const smoothScrollTo = useSmoothScroll()

    const smoothScroll = (e) => {
        e.preventDefault()
        const targetId = e.currentTarget.getAttribute('href').substring(1)
        smoothScrollTo(targetId)
    }

    function getFiltersStrFromFiltersObj(filtersObj) {
        let filtersStr = ''
        filtersObj && Object.keys(filtersObj).forEach(filter => filtersStr += `&${filter}=${filtersObj[filter]}`)
        return filtersStr
    }

    async function getSearchResults(searchInput, resultsSection: TypeOfResultValues, page?: number, filtersStr?: string) {
        const searchParam = searchInput ? `&search=${searchInput}` : ''
        const resultsSectionParam = resultsSection ? `&typeOfResult=${resultsSection}` : ''
        const pageParam = page ? `&page=${page}` : ''
        
        const splittedSearchParam = searchParam?.split('=')
        const encodedSearchParam = splittedSearchParam?.[1] ? encodeURIComponent(splittedSearchParam[1]) : ''
        const newSearchParam = encodedSearchParam ? splittedSearchParam[0] + '=' + encodedSearchParam : ''
        const res = await fetch(`${endpoint}?${newSearchParam}${resultsSectionParam}${pageParam}${filtersStr}`)
        const resJson = await res.json()
        return resJson
    }

    async function updateSearchResults(searchInput, resultsSection: TypeOfResultValues, setState, setHasNextPage, setTotal, filters) {
        if (!searchInput) return
        const filtersStr = getFiltersStrFromFiltersObj(filters)

        const res = await getSearchResults(searchInput, resultsSection, 1, filtersStr)

        res.hits?.length ? setState(res.hits) : setState([])
        res.total ? setTotal(res.total) : setTotal(0)
        setHasNextPage(res.hasNextPage)

        return res
    }

    async function handleFormSubmit(e) {
        e.preventDefault()
        const currentUrl = window.location.href
        const newUrl = new URL(currentUrl)
        const encodedSearchParam = encodeURIComponent(searchTextInput)
        newUrl.searchParams.set('search', encodedSearchParam)
        newUrl.searchParams.delete('productsSolutions')
        newUrl.searchParams.delete('documentationTypes')
        newUrl.searchParams.delete('articlesTypes')
        window.location.href = newUrl.toString()
    }

    async function handleLoadMore(typeOfResult: TypeOfResultValues, currentPage, setCurrentPage, setResults, setHasNextPage, shownSoFar: number) {
        if (loadingSection) return
        setLoadingSection(typeOfResult)
        try {
            const filtersObj = { solutions: selectedSolutions.join(','), documentationType: selectedDocumentationTypes.join(','), articleType: selectedArticleTypes.join(',') }
            const filtersStr = getFiltersStrFromFiltersObj(filtersObj)
            const loadMoreRes = await getSearchResults(searchParam, typeOfResult, currentPage + 1, filtersStr)
            setCurrentPage(prevState => prevState + 1)

            loadMoreRes.hits?.length && setResults(prevState => [...prevState, ...loadMoreRes.hits])
            setHasNextPage(loadMoreRes.hasNextPage)

            // the control is about to be removed; focus would fall to <body>
            if (!loadMoreRes.hasNextPage) pendingFocusRef.current = { section: typeOfResult, index: shownSoFar }

            announceResults(shownSoFar + (loadMoreRes.hits?.length || 0), loadMoreRes.total)
        } catch (error) {
            console.error(error)
        }
        setLoadingSection(null)
    }

    useEffect(() => {
        const pending = pendingFocusRef.current
        if (!pending) return
        pendingFocusRef.current = null

        const firstNew = gridRefs[pending.section].current?.children[pending.index] as HTMLElement
        firstNew?.querySelector('a')?.focus()
    }, [productsResults, documentationResults, articlesResults, otherResults])

    function handleCheckboxClick(value, setItems, setCurrentPage) {
        setCurrentPage(1)
        setItems(prevState => {
            if (prevState.indexOf(value) !== -1) {
                return prevState.filter(solution => solution !== value)
            } else {
                return [...prevState, value]
            }
        })
    }

    function getSearchParam() {
        const url = new URL(window.location.href)
        const params = new URLSearchParams(url.search)
        const searchInput = params.get('search')
        const decodedSearchInput = searchInput ? decodeURIComponent(searchInput) : ''
        return decodedSearchInput
    }

    useEffect(() => {
        const initialUrl = new URL(window.location.href)
        const initialParams = new URLSearchParams(initialUrl.search)
        const articlesTypesInitialParam = initialParams.get('articlesTypes')
        const documentationTypesInitialParam = initialParams.get('documentationTypes')
        const productsSolutionsInitialParam = initialParams.get('productsSolutions')

        const initialArticlesTypes = articlesTypesInitialParam?.split(',')
        const initialDocumentationTypes = documentationTypesInitialParam?.split(',')
        const initialProductsSolutions = productsSolutionsInitialParam?.split(',')

        initialArticlesTypes?.forEach(articleType => {
            handleCheckboxClick(articleType, setSelectedArticleTypes, setArticlesCurrentPage)
        })
        initialDocumentationTypes?.forEach(documentationType => {
            handleCheckboxClick(documentationType, setSelectedDocumentationTypes, setDocumentationCurrentPage)
        })
        initialProductsSolutions?.forEach(productSolution => {
            const solution = solutions.find(solutionObj => solutionObj.label === productSolution)
            solution && handleCheckboxClick(solution.value, setSelectedSolutions, setProductsCurrentPage)
        })
    }, [])

    useEffect(() => {
        const searchInput = getSearchParam()
        hasMounted && updateSearchResults(searchInput, 'products', setProductsResults, setHasProductsNextPage, setProductsTotal, { solutions: selectedSolutions.join(',') })
            .then(res => announceFilter('products', selectedSolutions.join(','), res))
    }, [selectedSolutions, hasMounted])

    useEffect(() => {
        const searchInput = getSearchParam()
        hasMounted && updateSearchResults(searchInput, 'documentation', setDocumentationResults, setHasDocumentationNextPage, setDocumentationTotal, { documentationType: selectedDocumentationTypes.join(',') })
            .then(res => announceFilter('documentation', selectedDocumentationTypes.join(','), res))
        
        // update wasDocumentationVisible whenever documentationTotal changes or filters change
        if (documentationTotal > 0 || selectedDocumentationTypes.length > 0) {
            setWasDocumentationVisible(true)
        } else if (documentationTotal === 0 && selectedDocumentationTypes.length === 0) {

            // reset after a short delay to prevent flickering
            const timer = setTimeout(() => {
                setWasDocumentationVisible(false)
            }, 1000)
            return () => clearTimeout(timer)
        }
    }, [selectedDocumentationTypes, hasMounted, documentationTotal])

    useEffect(() => {
        const searchInput = getSearchParam()
        hasMounted && updateSearchResults(searchInput, 'other', setOtherResults, setHasOtherNextPage, setOtherTotal, { otherType: selectedOtherTypes.join(',') })
            .then(res => announceFilter('other', selectedOtherTypes.join(','), res))
    }, [selectedOtherTypes, hasMounted])

    useEffect(() => {
        const searchInput = getSearchParam()
        hasMounted && updateSearchResults(searchInput, 'articles', setArticlesResults, setHasArticlesNextPage, setArticlesTotal, { articleType: selectedArticleTypes.join(',') })
            .then(res => announceFilter('articles', selectedArticleTypes.join(','), res))
    }, [selectedArticleTypes, hasMounted])

    useEffect(() => {
        const currentUrl = new URL(window.location.href)
        const params = new URLSearchParams(currentUrl.search)

        const solutionsObjs = selectedSolutions?.map(solutionId => solutions.find(solutionObj => solutionObj.value === solutionId))
        const solutionsLabels = solutionsObjs?.map(solutionObj => solutionObj.label)
        const solutionParam = solutionsLabels.length ? solutionsLabels.join(',') : ''

        const documentationParam = selectedDocumentationTypes?.length ? selectedDocumentationTypes.join(',') : ''

        const articlesParam = selectedArticleTypes?.length ? selectedArticleTypes.join(',') : ''

        solutionParam ? params.set('productsSolutions', solutionParam) : params.delete('productsSolutions')
        documentationParam ? params.set('documentationTypes', documentationParam) : params.delete('documentationTypes')
        articlesParam ? params.set('articlesTypes', articlesParam) : params.delete('articlesTypes')

        currentUrl.search = params.toString()
        window.history.replaceState({}, '', currentUrl.href)
    }, [selectedSolutions, selectedDocumentationTypes, selectedArticleTypes])

    useEffect(() => {
        const searchInput = getSearchParam()

        setHasMounted(true)
        setSearchParam(searchInput)
        setSearchTextInput(searchInput)
    }, [])

    function returnComponentTopPart() {
        return (
            <Section textColor='text-dark' className='top-section'>
                <div className='grid-container'>
                    <h1 className='title-lg top-section__title'>
                        {title}
                    </h1>
                    <div className='top-section__search'>
                        <form action='' onSubmit={handleFormSubmit}>
                            <button className='top-section__button'>
                                <svg width='18' height='18' fill='currentColor' viewBox='0 0 18 18' xmlns='http://www.w3.org/2000/svg'><path d='M8.0625 2.5C4.99042 2.5 2.5 4.99042 2.5 8.0625C2.5 11.1346 4.99042 13.625 8.0625 13.625C11.1346 13.625 13.625 11.1346 13.625 8.0625C13.625 4.99042 11.1346 2.5 8.0625 2.5ZM0.5 8.0625C0.5 3.88585 3.88585 0.5 8.0625 0.5C12.2392 0.5 15.625 3.88585 15.625 8.0625C15.625 12.2392 12.2392 15.625 8.0625 15.625C3.88585 15.625 0.5 12.2392 0.5 8.0625Z' /><path d='M12.7023 11.2881L17.9134 16.4992L16.4992 17.9134L11.2881 12.7023L12.7023 11.2881Z' /></svg>
                            </button>
                            <input
                                type='text'
                                placeholder={strings.placeholder}
                                className='top-section__input'
                                onChange={e => setSearchTextInput(e.target.value)}
                                value={searchTextInput}
                            />
                        </form>
                    </div>
                </div>
            </Section>
        )
    }

    if (!searchParam) return (
        <div className='page--search-results'>
            {returnComponentTopPart()}
        </div>
    )


    return (
        <div className='page--search-results'>
            {returnComponentTopPart()}

            <Section textColor='text-dark' className='results-tags'>
                <div className='grid-container'>
                    <div className='results-tags__wrapper'>

                        <Announcer message={announcement} />

                        <p className='results-tags__title'>
                            {productsTotal + documentationTotal + articlesTotal + otherTotal} {`${strings.resultsForCount} "${searchParam}"`}
                        </p>

                        <div className='results-tags__tags'>

                            {productsTotal ? <a href='#products' onClick={smoothScroll}>
                                Products ({productsTotal}) <svg width='11' height='13' fill='currentColor' viewBox='0 0 11 13' xmlns='http://www.w3.org/2000/svg'><path d='M6.18995 0H4.78995L4.78995 9.84338L0.989949 6.04338L0 7.03333L5.48995 12.5233L10.9799 7.03333L9.98995 6.04338L6.18995 9.84338L6.18995 0Z' /></svg>
                            </a> : <></>}

                            {documentationTotal ? <a href='#documentation' onClick={smoothScroll}>
                                Documentation ({documentationTotal}) <svg width='11' height='13' fill='currentColor' viewBox='0 0 11 13' xmlns='http://www.w3.org/2000/svg'><path d='M6.18995 0H4.78995L4.78995 9.84338L0.989949 6.04338L0 7.03333L5.48995 12.5233L10.9799 7.03333L9.98995 6.04338L6.18995 9.84338L6.18995 0Z' /></svg>
                            </a> : <></>}

                            {articlesTotal ? <a href='#articles' onClick={smoothScroll}>
                                Articles ({articlesTotal}) <svg width='11' height='13' fill='currentColor' viewBox='0 0 11 13' xmlns='http://www.w3.org/2000/svg'><path d='M6.18995 0H4.78995L4.78995 9.84338L0.989949 6.04338L0 7.03333L5.48995 12.5233L10.9799 7.03333L9.98995 6.04338L6.18995 9.84338L6.18995 0Z' /></svg>
                            </a> : <></>}

                            {otherTotal ? <a href='#other' onClick={smoothScroll}>
                                Other ({otherTotal}) <svg width='11' height='13' fill='currentColor' viewBox='0 0 11 13' xmlns='http://www.w3.org/2000/svg'><path d='M6.18995 0H4.78995L4.78995 9.84338L0.989949 6.04338L0 7.03333L5.48995 12.5233L10.9799 7.03333L9.98995 6.04338L6.18995 9.84338L6.18995 0Z' /></svg>
                            </a> : <></>}

                        </div>

                    </div>
                </div>
            </Section>

            {productsTotal ? <Section textColor='text-dark' className='results' id='products' ariaBusy={loadingSection === 'products'}>
                <div className='grid-container'>
                    <div className='grid-row grid-row-wrapper'>

                        <div className='c-md-3 results__sidebar'>

                            <p className='sidebar__title'>
                                Products ({productsTotal})
                            </p>

                            {solutions?.length ? <>
                                <button
                                    className={clsx(
                                        'sidebar__button',
                                        isSidebarProductsOpen && 'sidebar__button--active'
                                    )}
                                    onClick={toggleSidebarProducts}
                                >

                                    <span>
                                        {strings.filterBySolution}
                                    </span>

                                    <svg width='16' height='10' fill='currentColor' viewBox='0 0 16 10' xmlns='http://www.w3.org/2000/svg'><path d='M1.74997 0.227295L7.99997 6.4773L14.25 0.227295L15.5228 1.50009L7.99997 9.02288L0.477173 1.50009L1.74997 0.227295Z' /></svg>

                                </button>

                                <div className={clsx(
                                    'sidebar__accordion',
                                    isSidebarProductsOpen && 'sidebar__accordion--active'
                                )}>
                                    <div>
                                        <div>
                                            {solutions.map(solution => (
                                                <div className='checkbox' key={solution.value}>
                                                    <input type='checkbox'
                                                        id={solution.value}
                                                        onChange={() => handleCheckboxClick(solution.value, setSelectedSolutions, setProductsCurrentPage)}
                                                        checked={selectedSolutions.some(selectedSolution => selectedSolution === solution.value)}
                                                    />
                                                    <label htmlFor={solution.value}>
                                                        <span className='checkbox__box'>
                                                            <svg width='13' height='10' fill='currentColor' viewBox='0 0 13 10' xmlns='http://www.w3.org/2000/svg'><path d='M12.4899 1.49996L4.49996 9.48991L0.0100098 4.99996L0.999959 4.01001L4.49996 7.51001L11.5 0.51001L12.4899 1.49996Z' /></svg>
                                                        </span>
                                                        <span className='checkbox__text'>
                                                            {solution.label}
                                                        </span>
                                                    </label>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            </> : <></>}

                        </div>

                        <div className='c-md-9 results__products'>

                            <div className='grid-row' ref={gridRefs.products}>
                                {productsResults.map(item => (
                                    <div className='c-6 c-sm-4' key={item.url}>
                                        <ProductCard
                                            href={{
                                                url: item.url,
                                            }}
                                            img={item.image?.link && {
                                                src: item.image.link,
                                                alt: item.image.alt
                                            }}
                                            name={item.title}
                                            partNumber={item.partNumber}
                                        />
                                    </div>
                                ))}
                            </div>

                            {hasProductsNextPage ? <div className='results__load-more'>
                                <button
                                    onClick={() => handleLoadMore('products', productsCurrentPage, setProductsCurrentPage, setProductsResults, setHasProductsNextPage, productsResults.length)}
                                    aria-busy={loadingSection === 'products' ? true : undefined}
                                    aria-disabled={loadingSection ? true : undefined}
                                >
                                    {loadingSection === 'products' ? strings.loading : <>{strings.loadMore} <svg width='13' height='14' fill='currentColor' viewBox='0 0 13 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.19999 0.800049V6.30005H12.7V7.70005H7.19999V13.2H5.79999V7.70005H0.299988V6.30005H5.79999V0.800049H7.19999Z' /></svg></>}
                                </button>
                            </div> : <></>}

                        </div>

                    </div>
                </div>
            </Section> : <></>}

            {(documentationTotal > 0 || selectedDocumentationTypes.length > 0 || wasDocumentationVisible) ? <Section textColor='text-dark' className='results' id='documentation' ariaBusy={loadingSection === 'documentation'}>
                <div className='grid-container'>
                    <div className='grid-row grid-row-wrapper'>

                        <div className='c-md-3 results__sidebar'>

                            <p className='sidebar__title'>
                                Documentation ({documentationTotal})
                            </p>

                            {documentationTypes?.length ? <button
                                className={clsx(
                                    'sidebar__button',
                                    isSidebarDocumentationOpen && 'sidebar__button--active'
                                )}
                                onClick={toggleSidebarDocumentation}
                            >

                                <span>
                                    {strings.filterByType}
                                </span>

                                <svg width='16' height='10' fill='currentColor' viewBox='0 0 16 10' xmlns='http://www.w3.org/2000/svg'><path d='M1.74997 0.227295L7.99997 6.4773L14.25 0.227295L15.5228 1.50009L7.99997 9.02288L0.477173 1.50009L1.74997 0.227295Z' /></svg>

                            </button> : <></>}

                            <div className={clsx(
                                'sidebar__accordion',
                                isSidebarDocumentationOpen && 'sidebar__accordion--active'
                            )}>
                                <div>
                                    <div>
                                        {documentationTypes?.length ? documentationTypes.map(docType => (
                                            <div className='checkbox' key={docType}>

                                                <input type='checkbox'
                                                    id={docType}
                                                    onChange={() => handleCheckboxClick(docType, setSelectedDocumentationTypes, setDocumentationCurrentPage)}
                                                    checked={selectedDocumentationTypes.some(selectedDocumentation => selectedDocumentation === docType)}
                                                />

                                                <label htmlFor={docType}>

                                                    <span className='checkbox__box'>
                                                        <svg width='13' height='10' fill='currentColor' viewBox='0 0 13 10' xmlns='http://www.w3.org/2000/svg'><path d='M12.4899 1.49996L4.49996 9.48991L0.0100098 4.99996L0.999959 4.01001L4.49996 7.51001L11.5 0.51001L12.4899 1.49996Z' /></svg>
                                                    </span>

                                                    <span className='checkbox__text'>
                                                        {docType}
                                                    </span>

                                                </label>
                                            </div>
                                        )) : <></>}
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className='c-md-9 results__documentation'>

                            {documentationHelperText && (
                                <p className='results__helper-text'>
                                    {documentationHelperText}
                                </p>
                            )}

                            {documentationTotal > 0 ? (
                                <>
                                    <div className='documentation-grid' ref={gridRefs.documentation}>
                                        {documentationResults.map(item => (
                                            <DocumentationLine
                                                key={item.url}
                                                href={item.url}
                                                name={item.title}
                                                date={item.date && dayjs(item.date).format('DD.MM.YY')}
                                                category={item.type}
                                                size={item.size + ' KB'}
                                                isRestricted={item.isRestricted}
                                            />
                                        ))}
                                    </div>

                                    {hasDocumentationNextPage ? <div className='results__load-more'>
                                        <button
                                    onClick={() => handleLoadMore('documentation', documentationCurrentPage, setDocumentationCurrentPage, setDocumentationResults, setHasDocumentationNextPage, documentationResults.length)}
                                    aria-busy={loadingSection === 'documentation' ? true : undefined}
                                    aria-disabled={loadingSection ? true : undefined}
                                >
                                            {loadingSection === 'documentation' ? strings.loading : <>{strings.loadMore} <svg width='13' height='14' fill='currentColor' viewBox='0 0 13 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.19999 0.800049V6.30005H12.7V7.70005H7.19999V13.2H5.79999V7.70005H0.299988V6.30005H5.79999V0.800049H7.19999Z' /></svg></>}
                                        </button>
                                    </div> : <></>}
                                </>
                            ) : (
                                <p className='results__no-results'>
                                    No results found
                                </p>
                            )}

                        </div>

                    </div>
                </div>
            </Section> : <></>}

            {articlesTotal ? <Section textColor='text-dark' className='results' id='articles' ariaBusy={loadingSection === 'articles'}>
                <div className='grid-container'>
                    <div className='grid-row grid-row-wrapper'>
                        <div className='c-md-3 results__sidebar'>
                            <p className='sidebar__title'>
                                Articles ({articlesTotal})
                            </p>

                            <button
                                className={clsx(
                                    'sidebar__button',
                                    isSidebarArticlesOpen && 'sidebar__button--active'
                                )}
                                onClick={toggleSidebarArticles}
                            >

                                <span>
                                    {strings.filterByType}
                                </span>

                                <svg width='16' height='10' fill='currentColor' viewBox='0 0 16 10' xmlns='http://www.w3.org/2000/svg'><path d='M1.74997 0.227295L7.99997 6.4773L14.25 0.227295L15.5228 1.50009L7.99997 9.02288L0.477173 1.50009L1.74997 0.227295Z' /></svg>

                            </button>

                            <div className={clsx(
                                'sidebar__accordion',
                                isSidebarArticlesOpen && 'sidebar__accordion--active'
                            )}>
                                <div>
                                    <div>
                                        {articlesTypes.map(({ value, label }) => (
                                            <div className='checkbox' key={value}>
                                                <input
                                                    type='checkbox'
                                                    id={value}
                                                    onChange={() => handleCheckboxClick(value, setSelectedArticleTypes, setArticlesCurrentPage)}
                                                    checked={selectedArticleTypes.some(selectedArticleType => selectedArticleType === value)}
                                                />
                                                <label htmlFor={value}>
                                                    <span className='checkbox__box'>
                                                        <svg width='13' height='10' fill='currentColor' viewBox='0 0 13 10' xmlns='http://www.w3.org/2000/svg'><path d='M12.4899 1.49996L4.49996 9.48991L0.0100098 4.99996L0.999959 4.01001L4.49996 7.51001L11.5 0.51001L12.4899 1.49996Z' /></svg>
                                                    </span>
                                                    <span className='checkbox__text'>
                                                        {label}
                                                    </span>
                                                </label>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            </div>

                        </div>

                        <div className='c-md-9 results__articles'>

                            <div className='articles-grid' ref={gridRefs.articles}>
                                {articlesResults.map(item => {
                                    if (item.contentType === 'success-story') {
                                        return (
                                            <SuccessStoriesCard
                                                key={item.url}
                                                featured={false}
                                                href={{ url: item.url }}
                                                img={item.image?.link && {
                                                    src: item.image.link,
                                                    alt: item.image.alt
                                                }}
                                                subtitle={item.type}
                                                title={item.title}
                                            />
                                        )
                                    }
                                    return (
                                        <ArticleCardNew
                                            key={item.url}
                                            featured={false}
                                            href={{ url: item.url }}
                                            img={item.image?.link && {
                                                src: item.image.link,
                                                alt: item.image.alt
                                            }}
                                            category={item.type}
                                            customCategory={item.solution && [item.solution]}
                                            title={item.title}
                                        />
                                    )
                                })}
                            </div>

                            {hasArticlesNextPage ? <div className='results__load-more'>
                                <button
                                    onClick={() => handleLoadMore('articles', articlesCurrentPage, setArticlesCurrentPage, setArticlesResults, setHasArticlesNextPage, articlesResults.length)}
                                    aria-busy={loadingSection === 'articles' ? true : undefined}
                                    aria-disabled={loadingSection ? true : undefined}
                                >
                                    {loadingSection === 'articles' ? strings.loading : <>{strings.loadMore} <svg width='13' height='14' fill='currentColor' viewBox='0 0 13 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.19999 0.800049V6.30005H12.7V7.70005H7.19999V13.2H5.79999V7.70005H0.299988V6.30005H5.79999V0.800049H7.19999Z' /></svg></>}
                                </button>
                            </div> : <></>}

                        </div>

                    </div>
                </div>
            </Section> : <></>}

            {otherTotal ? <Section textColor='text-dark' className='results' id='other' ariaBusy={loadingSection === 'other'}>
                <div className='grid-container'>
                    <div className='grid-row grid-row-wrapper'>
                        <div className='c-md-3 results__sidebar'>
                            <p className='sidebar__title'>
                                Other ({otherTotal})
                            </p>
                        </div>

                        <div className='c-md-9 results__other'>

                            <div className='other-grid' ref={gridRefs.other}>
                                {otherResults.map(item => (
                                    <OtherLine
                                        key={item.url}
                                        href={item.url}
                                        name={item.title}
                                        description={item.description}
                                    />
                                ))}
                            </div>

                            {hasOtherNextPage ? <div className='results__load-more'>
                                <button
                                    onClick={() => handleLoadMore('other', otherCurrentPage, setOtherCurrentPage, setOtherResults, setHasOtherNextPage, otherResults.length)}
                                    aria-busy={loadingSection === 'other' ? true : undefined}
                                    aria-disabled={loadingSection ? true : undefined}
                                >
                                    {loadingSection === 'other' ? strings.loading : <>{strings.loadMore} <svg width='13' height='14' fill='currentColor' viewBox='0 0 13 14' xmlns='http://www.w3.org/2000/svg'><path d='M7.19999 0.800049V6.30005H12.7V7.70005H7.19999V13.2H5.79999V7.70005H0.299988V6.30005H5.79999V0.800049H7.19999Z' /></svg></>}
                                </button>
                            </div> : <></>}

                        </div>

                    </div>
                </div>
            </Section> : <></>}

        </div>
    )
}